import { useEffect, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { createUuid } from "../../../lib/trace/context";
import { executePointMerge, fetchPointMergeCandidates, pointMergeSelectionKey, previewPointMerge } from "../service/resourcePointMergeService";
import type { ResourcePointSpuItem } from "../types";
import type { PointMergeExecute, PointMergePreview, PointMergeResult } from "../types/merge";

/** 编排混合选择、预览失效及带幂等号的确认提交。 */
export function useResourcePointMergeRuntime() {
  const queryClient = useQueryClient();
  const [keyword, setKeyword] = useState("");
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState<ResourcePointSpuItem[]>([]);
  const [targetId, setTargetId] = useState("");
  const [preview, setPreview] = useState<PointMergePreview | null>(null);
  const [result, setResult] = useState<PointMergeResult | null>(null);
  const [error, setError] = useState("");
  const [previewing, setPreviewing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const revision = useRef(0);
  const busy = useRef(false);
  const alive = useRef(true);
  const approved = useRef<{ key: string; payload: PointMergeExecute } | null>(null);
  const selection = useRef({ selected, targetId });
  const candidates = useQuery({
    queryKey: ["resource-point-merge", keyword, page],
    queryFn: () => fetchPointMergeCandidates(keyword, page),
  });
  useEffect(() => {
    alive.current = true;
    return () => { alive.current = false; revision.current += 1; };
  }, []);

  /** 同步清空预览凭据，迟到的响应不能恢复旧选择。 */
  function invalidatePreview() {
    revision.current += 1;
    approved.current = null;
    setPreview(null);
    setPreviewing(false);
    setError("");
  }

  /** 跨页保存所选记录，首次添加默认作为保留记录。 */
  function toggleRecord(row: ResourcePointSpuItem) {
    if (busy.current) return;
    const current = selection.current;
    const removing = current.selected.some((item) => item.resourcePointSpuId === row.resourcePointSpuId);
    if (!removing && current.selected.length >= 20) { setError("每次最多合并 20 条记录"); return; }
    const next = removing ? current.selected.filter((item) => item.resourcePointSpuId !== row.resourcePointSpuId) : [...current.selected, row];
    const nextTarget = next.some((item) => item.resourcePointSpuId === current.targetId) ? current.targetId : next[0]?.resourcePointSpuId ?? "";
    selection.current = { selected: next, targetId: nextTarget };
    setSelected(next); setTargetId(nextTarget); setResult(null); invalidatePreview();
  }

  /** 指定最终保留记录，其名称、分类和主档资料决定合并结果。 */
  function chooseTarget(id: string) {
    if (busy.current || !selection.current.selected.some((item) => item.resourcePointSpuId === id)) return;
    selection.current = { ...selection.current, targetId: id };
    setTargetId(id); invalidatePreview();
  }

  /** 提交当前选择的预览，选择变化或卸载后丢弃响应。 */
  async function requestPreview() {
    if (busy.current) return;
    const { selected: rows, targetId: target } = selection.current;
    if (rows.length < 2 || !target) return;
    invalidatePreview();
    const currentRevision = revision.current;
    const sourceIds = rows.filter((item) => item.resourcePointSpuId !== target).map((item) => item.resourcePointSpuId);
    setPreviewing(true);
    try {
      const next = await previewPointMerge({ targetId: target, sourceIds });
      if (!alive.current || currentRevision !== revision.current) return;
      const expected = new Set(sourceIds);
      if (next.target.resourcePointSpuId !== target || next.sources.length !== sourceIds.length || next.sources.some((item) => !expected.has(item.resourcePointSpuId))) {
        throw new Error("预览与当前选择不一致，请重新预览");
      }
      approved.current = { key: pointMergeSelectionKey(target, sourceIds), payload: {
        targetId: target, sourceIds, previewToken: next.previewToken, requestId: createUuid(),
      } };
      setPreview(next);
    } catch (cause) {
      if (alive.current && currentRevision === revision.current) setError(cause instanceof Error ? cause.message : "合并预览失败");
    } finally {
      if (alive.current && currentRevision === revision.current) setPreviewing(false);
    }
  }

  /** 使用同一请求号提交；瞬时重复点击只发送一次，断线重试保留原请求号。 */
  async function confirmMerge() {
    const confirmation = approved.current;
    const current = selection.current;
    const key = pointMergeSelectionKey(current.targetId, current.selected.filter((item) => item.resourcePointSpuId !== current.targetId).map((item) => item.resourcePointSpuId));
    if (busy.current || !confirmation || confirmation.key !== key || !preview || preview.blockers.length) return false;
    busy.current = true; setSubmitting(true); setError("");
    try {
      const saved = await executePointMerge(confirmation.payload);
      if (alive.current) {
        setResult(saved); invalidatePreview();
        selection.current = { selected: [], targetId: "" }; setSelected([]); setTargetId("");
      }
      await queryClient.invalidateQueries({ queryKey: ["resource-point-merge"] });
      await queryClient.invalidateQueries({ queryKey: ["resource-point-management"] });
      return true;
    } catch (cause) {
      if (alive.current) setError(cause instanceof Error ? cause.message : "合并提交失败，请重试");
      return false;
    } finally {
      busy.current = false;
      if (alive.current) setSubmitting(false);
    }
  }

  return { candidates, keyword, page, selected, targetId, preview, result, error, previewing, submitting,
    search: (value: string) => { setKeyword(value); setPage(1); }, setPage, toggleRecord, chooseTarget,
    requestPreview, confirmMerge, closePreview: () => { if (!busy.current) invalidatePreview(); } };
}
